import client from './client'
import type {
  ExternalVideoPayload,
  GalleryPayload,
  GalleryStorageSummary,
  OrganizerGallery,
  OrganizerGalleryItem,
  ReserveCoverUploadPayload,
  ReserveUploadPayload,
  SignedParts,
  UpdateGalleryPayload,
  UpdateItemPayload,
  UploadTicket,
} from '@/types/galleries'

const BASE = '/organizer/events'

function galleryBase(eventSlug: string, galleryId?: string): string {
  const root = `${BASE}/${eventSlug}/galleries`
  return galleryId === undefined ? root : `${root}/${galleryId}`
}

export async function fetchGalleryStorage(eventSlug: string): Promise<GalleryStorageSummary> {
  const { data } = await client.get<{ data: GalleryStorageSummary }>(`${BASE}/${eventSlug}/gallery-storage`)
  return data.data
}

export async function listGalleries(eventSlug: string): Promise<OrganizerGallery[]> {
  const { data } = await client.get<{ data: OrganizerGallery[] }>(galleryBase(eventSlug))
  return data.data
}

export async function createGallery(eventSlug: string, payload: GalleryPayload): Promise<OrganizerGallery> {
  const { data } = await client.post<{ data: OrganizerGallery }>(galleryBase(eventSlug), payload)
  return data.data
}

export async function reorderGalleries(eventSlug: string, ids: string[]): Promise<OrganizerGallery[]> {
  const { data } = await client.put<{ data: OrganizerGallery[] }>(`${galleryBase(eventSlug)}/reorder`, { ids })
  return data.data
}

export async function getGallery(eventSlug: string, galleryId: string): Promise<OrganizerGallery> {
  const { data } = await client.get<{ data: OrganizerGallery }>(galleryBase(eventSlug, galleryId))
  return data.data
}

export async function updateGallery(
  eventSlug: string,
  galleryId: string,
  payload: UpdateGalleryPayload,
): Promise<OrganizerGallery> {
  const { data } = await client.patch<{ data: OrganizerGallery }>(galleryBase(eventSlug, galleryId), payload)
  return data.data
}

export async function deleteGallery(eventSlug: string, galleryId: string): Promise<void> {
  await client.delete(galleryBase(eventSlug, galleryId))
}

export async function listGalleryItems(
  eventSlug: string,
  galleryId: string,
  ids?: string[],
): Promise<OrganizerGalleryItem[]> {
  const { data } = await client.get<{ data: OrganizerGalleryItem[] }>(
    `${galleryBase(eventSlug, galleryId)}/items`,
    { params: ids ? { ids } : undefined },
  )
  return data.data
}

export async function reorderGalleryItems(
  eventSlug: string,
  galleryId: string,
  ids: string[],
): Promise<OrganizerGalleryItem[]> {
  const { data } = await client.put<{ data: OrganizerGalleryItem[] }>(
    `${galleryBase(eventSlug, galleryId)}/items/reorder`,
    { ids },
  )
  return data.data
}

export async function bulkDeleteGalleryItems(
  eventSlug: string,
  galleryId: string,
  ids: string[],
): Promise<void> {
  await client.post(`${galleryBase(eventSlug, galleryId)}/items/bulk-delete`, { ids })
}

export async function bulkMoveGalleryItems(
  eventSlug: string,
  galleryId: string,
  targetGalleryId: string,
  ids: string[],
): Promise<OrganizerGallery> {
  const { data } = await client.post<{ data: OrganizerGallery }>(
    `${galleryBase(eventSlug, galleryId)}/items/bulk-move`,
    { target_gallery_id: targetGalleryId, ids },
  )
  return data.data
}

export async function bulkSetItemsHidden(
  eventSlug: string,
  galleryId: string,
  ids: string[],
  isHidden: boolean,
): Promise<OrganizerGalleryItem[]> {
  const { data } = await client.post<{ data: OrganizerGalleryItem[] }>(
    `${galleryBase(eventSlug, galleryId)}/items/bulk-visibility`,
    { ids, is_hidden: isHidden },
  )
  return data.data
}

export async function updateGalleryItem(
  eventSlug: string,
  galleryId: string,
  itemId: string,
  payload: UpdateItemPayload,
): Promise<OrganizerGalleryItem> {
  const { data } = await client.patch<{ data: OrganizerGalleryItem }>(
    `${galleryBase(eventSlug, galleryId)}/items/${itemId}`,
    payload,
  )
  return data.data
}

export async function deleteGalleryItem(eventSlug: string, galleryId: string, itemId: string): Promise<void> {
  await client.delete(`${galleryBase(eventSlug, galleryId)}/items/${itemId}`)
}

export async function retryGalleryItem(
  eventSlug: string,
  galleryId: string,
  itemId: string,
): Promise<OrganizerGalleryItem> {
  const { data } = await client.post<{ data: OrganizerGalleryItem }>(
    `${galleryBase(eventSlug, galleryId)}/items/${itemId}/retry`,
  )
  return data.data
}

export async function addExternalVideo(
  eventSlug: string,
  galleryId: string,
  payload: ExternalVideoPayload,
): Promise<OrganizerGalleryItem> {
  const { data } = await client.post<{ data: OrganizerGalleryItem }>(
    `${galleryBase(eventSlug, galleryId)}/external-videos`,
    payload,
  )
  return data.data
}

export async function reserveGalleryUpload(
  eventSlug: string,
  galleryId: string,
  payload: ReserveUploadPayload,
): Promise<UploadTicket> {
  const { data } = await client.post<{ data: UploadTicket }>(
    `${galleryBase(eventSlug, galleryId)}/uploads`,
    payload,
  )
  return data.data
}

export async function signGalleryUploadParts(
  eventSlug: string,
  galleryId: string,
  uploadId: string,
  partNumbers: number[],
): Promise<SignedParts> {
  const { data } = await client.post<{ data: SignedParts }>(
    `${galleryBase(eventSlug, galleryId)}/uploads/${uploadId}/parts`,
    { part_numbers: partNumbers },
  )
  return data.data
}

export async function completeGalleryUpload(
  eventSlug: string,
  galleryId: string,
  uploadId: string,
  posterUploaded: boolean,
): Promise<OrganizerGalleryItem> {
  const { data } = await client.post<{ data: OrganizerGalleryItem }>(
    `${galleryBase(eventSlug, galleryId)}/uploads/${uploadId}/complete`,
    { poster_uploaded: posterUploaded },
  )
  return data.data
}

export async function abortGalleryUpload(
  eventSlug: string,
  galleryId: string,
  uploadId: string,
): Promise<void> {
  await client.delete(`${galleryBase(eventSlug, galleryId)}/uploads/${uploadId}`)
}

export async function reserveGalleryCoverUpload(
  eventSlug: string,
  galleryId: string,
  payload: ReserveCoverUploadPayload,
): Promise<UploadTicket> {
  const { data } = await client.post<{ data: UploadTicket }>(
    `${galleryBase(eventSlug, galleryId)}/cover-upload`,
    payload,
  )
  return data.data
}

export async function completeGalleryCoverUpload(
  eventSlug: string,
  galleryId: string,
  uploadId: string,
): Promise<OrganizerGallery> {
  const { data } = await client.post<{ data: OrganizerGallery }>(
    `${galleryBase(eventSlug, galleryId)}/cover-upload/${uploadId}/complete`,
  )
  return data.data
}

export async function deleteGalleryCover(eventSlug: string, galleryId: string): Promise<void> {
  await client.delete(`${galleryBase(eventSlug, galleryId)}/cover`)
}
